
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorsList } from "@/components/ContractorsList";
import { Contractor } from "@/types/contractor";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";

const BestRoofersPage = () => {
  const serviceType = "Roofers";
  const pageTitle = `Best ${serviceType} in Oklahoma City, OK`;

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'roofing'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Roofing')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: serviceType, href: "/best-roofers-oklahoma-city" },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": "Find top-rated, established roofers in Oklahoma City for residential and commercial roofing services. Free quotes, trusted professionals.",
    "url": "https://homeprosokc.com/best-roofers-oklahoma-city",
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={pageTitle}
        description="Find top-rated, established roofers in Oklahoma City for residential and commercial roofing services. Free quotes, trusted professionals."
        canonicalUrl="/best-roofers-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title={pageTitle}
          subtitle="Connect with established, reputable roofers for all your residential and commercial roofing needs."
          buttonText="Get a Quote"
          buttonLink="#quote-request"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Why Choose Our Roofers</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Established Professionals</h3>
                <p>Our roofers are well-established in the Oklahoma City area with proven experience and expertise.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Emergency Services</h3>
                <p>Many of our professionals offer emergency roofing services for storm damage, leaks, and other urgent situations.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Free Estimates</h3>
                <p>Get detailed, no-obligation quotes for your roofing projects from our network of trusted professionals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our Roofing Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Roof inspections and assessments</li>
                  <li>Roof repairs and maintenance</li>
                  <li>Complete roof replacements</li>
                  <li>New roof installations</li>
                  <li>Storm damage repairs</li>
                  <li>Skylight installation and repair</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Commercial roof inspections</li>
                  <li>Flat roof installation and repair</li>
                  <li>TPO and EPDM roofing systems</li>
                  <li>Metal roofing for commercial buildings</li>
                  <li>Commercial roof maintenance programs</li>
                  <li>Insurance claim assistance</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quote Request Form */}
        <QuoteRequestForm serviceName={pageTitle} />
        
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Roofers in Oklahoma City</h2>
          <ContractorsList
            contractors={contractors}
            isLoading={isLoading}
            viewMode="grid"
            onViewModeChange={() => {}}
            onServiceTypeChange={() => {}}
            onLocationChange={() => {}}
            selectedServiceType=""
          />
        </div>
      </div>
    </div>
  );
};

export default BestRoofersPage;
